import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL, fetchFile } from "@ffmpeg/util";
import fileImportStore from '@/store/fileImport.store';
import { IFileInfo } from "@/types/fileImport";

/**
 * 将时间字符串转换为时长时间戳（以秒为单位）
 * @param timeString 时间字符串，格式为 "HH:MM:SS.SS"
 * @returns 时长时间戳（以秒为单位）
 */
function convertToTimestamp(timeString: string): number {
  const timeParts = timeString.split(":");
  if (timeParts.length !== 3) {
    throw new Error('Invalid time format. Expected format: "HH:MM:SS.SS"');
  }

  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);
  const seconds = parseFloat(timeParts[2]);

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  return totalSeconds;
}

/**
 * 加载 FFmpeg
 */
export async function loadFFmpeg() {
  let ffmpeg = fileImportStore.getState().ffmpeg;
  if (ffmpeg) return;
  const baseURL = "";
  ffmpeg = new FFmpeg();
  // toBlobURL is used to bypass CORS issue, urls with the same
  // domain can be used directly.
  console.log("ffmpeg start load");
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
    wasmURL: await toBlobURL(
      `${baseURL}/ffmpeg-core.wasm`,
      "application/wasm"
    ),
  });
  console.log("ffmpeg loaded");
  fileImportStore.getState().setFFmpeg(ffmpeg);
}

/**
 * 清理 FFmpeg 实例
 */
export async function clearFFmpeg() {
  const ffmpeg = fileImportStore.getState().ffmpeg;
  if (ffmpeg) {
    ffmpeg.terminate();
  }
}

/**
 * 获取音频文件信息
 * @param inputFile 输入文件
 * @param uid 文件唯一标识
 * @returns 音频文件信息（时长和声道数）
 */
export async function getAudioFileInfo(inputFile: File, uid: string): Promise<IFileInfo> {
  // eslint-disable-next-line no-async-promise-executor
  let ffmpeg = fileImportStore.getState().ffmpeg;
  if (!ffmpeg) {
    await loadFFmpeg();
    ffmpeg = fileImportStore.getState().ffmpeg;
  }
  // 捕获日志输出
  let logContent = '';

  ffmpeg?.on("log", ({ message }: { message: string }) => {
    logContent += message + '\n';
  });
  const newFileName = uid + inputFile.name;
  await ffmpeg?.writeFile(newFileName, await fetchFile(inputFile));
  await ffmpeg?.exec(["-i", newFileName]);
  // 读取结束标志符
  const fileLogs = logContent.split('Aborted');
  const targetFileLog = fileLogs.find(item => item.includes(newFileName));
  // 还没结束
  if (!targetFileLog) {
    return getAudioFileInfo(inputFile, uid);
  }
  const durationMatch = targetFileLog.match(/Duration: (\d+:\d+:\d+\.\d+)/);
  const channelsMatch = targetFileLog.match(
    /Audio:.*?(\d+) Hz, (stereo|mono)/
  );

  const duration = durationMatch && durationMatch[1]
    ? convertToTimestamp(durationMatch[1])
    : 0;

  const channels = channelsMatch ? channelsMatch[2] === "stereo" ? 2 : 1 : 0;

  logContent = logContent.replace(targetFileLog, '');
  await ffmpeg?.deleteFile(newFileName);
  return ({
    duration,
    channels,
  });
}

/**
 * 将音频文件转换为 MP3 格式
 * @param inputFile 输入文件
 * @param uid 文件唯一标识
 * @returns 转换后的 MP3 文件
 */
export async function convertMp3File(inputFile: File, uid: string): Promise<File> {
  if (inputFile.type === "audio/mpeg" || inputFile.type === "audio/mp3") {
    console.log('already mp3');
    return inputFile;
  }

  let ffmpeg = fileImportStore.getState().ffmpeg;
  if (!ffmpeg) {
    await loadFFmpeg();
    ffmpeg = fileImportStore.getState().ffmpeg;
  }
  const newFileName = `${uid}.mp3`;
  const newOutputFileName = `output-${uid}.mp3`;
  await ffmpeg?.writeFile(newFileName, await fetchFile(inputFile));
  await ffmpeg?.exec(["-i", newFileName, newOutputFileName]);

  // 读取输出文件
  const fileData = await ffmpeg?.readFile(newOutputFileName);
  if (!fileData) {
    throw new Error('Failed to read output file');
  }
  const data = new Uint8Array(fileData as unknown as ArrayBuffer);
  const outputBlob = new Blob([data.buffer], { type: 'audio/mp3' });
  const outputFile = new File([outputBlob], inputFile.name, {
    type: 'audio/mp3'
  });
  await ffmpeg?.deleteFile(newFileName);
  await ffmpeg?.deleteFile(newOutputFileName);
  return outputFile;
}

/**
 * 将音频文件转换为单声道 MP3 格式
 * @param inputFile 输入文件
 * @param uid 文件唯一标识
 * @returns 转换后的单声道 MP3 文件
 */
export async function convertMonoMp3File(inputFile: File, uid: string): Promise<File> {
  let ffmpeg = fileImportStore.getState().ffmpeg;
  if (!ffmpeg) {
    await loadFFmpeg();
    ffmpeg = fileImportStore.getState().ffmpeg;
  }

  const newFileName = `${uid}.mp3`;
  const newOutputFileName = `output-${uid}.mp3`;
  await ffmpeg?.writeFile(newFileName, await fetchFile(inputFile));
  await ffmpeg?.exec(["-i", newFileName, "-ac", "1", newOutputFileName]);

  // 读取输出文件
  const fileData = await ffmpeg?.readFile(newOutputFileName);
  if (!fileData) {
    throw new Error('Failed to read output file');
  }
  const data = new Uint8Array(fileData as unknown as ArrayBuffer);
  const outputBlob = new Blob([data.buffer], { type: 'audio/mp3' });
  const outputFile = new File([outputBlob], inputFile.name, {
    type: 'audio/mp3',
  });
  await ffmpeg?.deleteFile(newFileName);
  await ffmpeg?.deleteFile(newOutputFileName);
  return outputFile;
}
